import React from 'react';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import {
	type ExtensionManifest,
	NATIVE_EMBED_EXTENSION_KEY,
	NATIVE_EMBED_EXTENSION_TYPE,
} from '@atlaskit/editor-common/extensions';
import type { PublicPluginAPI, CorePlugin, NextEditorPlugin } from '@atlaskit/editor-common/types';
import type { LocalIdPlugin } from '@atlaskit/editor-plugin-local-id';
import type { CardClient } from '@atlaskit/link-provider';
import {
	DEFAULT_ALIGNMENT,
	type NativeEmbedParameterValues,
	setParameters,
} from '@atlaskit/native-embeds-common';
import { fg } from '@atlaskit/platform-feature-flags';
import type { MalleableUiPlugin } from '@atlassian/editor-plugin-malleable-ui/malleableUiPluginType';
import { getNativeEmbedFacade } from '@atlassian/native-embeds-core';
import type { ExperienceManifest } from '@atlassian/native-embeds-core/types';
import { DBExperienceManifest } from '@atlassian/native-embeds-db-experience';
import { DBEntryExperienceManifest } from '@atlassian/native-embeds-db-experience/entry-experience';
import {
	MauiExperienceManifest,
	MAUI_EXPERIENCE_MANIFEST_ID,
} from '@atlassian/native-embeds-maui-experience';
import {
	MauiWhiteboardExperienceManifest,
	MAUI_WHITEBOARD_EXPERIENCE_MANIFEST_ID,
} from '@atlassian/native-embeds-maui-whiteboard-experience';
import { PageExperienceManifest } from '@atlassian/native-embeds-page-experience';
import { SlideExperienceManifest } from '@atlassian/native-embeds-slide-experience';
import { WhiteboardExperienceManifest } from '@atlassian/native-embeds-whiteboard-experience';

import type { NativeEmbedOverlayRenderer } from './overlay';

/** Minimal local mirror of AI plugin shared state to avoid a hard dep on `@atlassian/editor-plugin-ai`. */
type AIExperienceSharedState = {
	aiDecorationExperiencePluginState: {
		inlineRovoStreamingTargetId?: string | null;
	};
};

type AIExperiencePluginType = NextEditorPlugin<
	'aiExperience',
	{ sharedState: AIExperienceSharedState | undefined }
>;

type NativeEmbedsEditorAPI = PublicPluginAPI<
	[CorePlugin, LocalIdPlugin, AIExperiencePluginType, MalleableUiPlugin]
>;

/**
 * IDs of experiences that are always enabled regardless of the
 * `platform_native_embeds_rollout_non_maui_experience` feature gate.
 * MauiWhiteboardExperienceManifest is included because it only matches
 * whiteboard URLs with `isRemixDiagram=true` (i.e. diagrams created via the MAUI flow).
 */
const alwaysEnabledExperienceIds = new Set([
	MAUI_EXPERIENCE_MANIFEST_ID,
	MAUI_WHITEBOARD_EXPERIENCE_MANIFEST_ID,
]);

const allExperiences = [
	DBEntryExperienceManifest,
	DBExperienceManifest,
	MauiExperienceManifest,
	PageExperienceManifest,
	SlideExperienceManifest,
	// MauiWhiteboardExperienceManifest must come before WhiteboardExperienceManifest
	// so its more specific isUrlMatch (requiring isRemixDiagram=true) is checked first.
	MauiWhiteboardExperienceManifest,
	WhiteboardExperienceManifest,
] satisfies [ExperienceManifest, ...ExperienceManifest[]];

const gatedExperiences = allExperiences.map((experience: ExperienceManifest) => ({
	...experience,
	isUrlMatch: (url: string): boolean => {
		if (!fg('platform_native_embeds_rollout_non_maui_experience')) {
			if (alwaysEnabledExperienceIds.has(experience.id)) {
				return experience.isUrlMatch(url);
			}
			return false;
		}
		return experience.isUrlMatch(url);
	},
})) as [ExperienceManifest, ...ExperienceManifest[]];

// Register all experience manifests at module-load time.
// This is intentionally module-level (not inside a React lifecycle) because the
// facade must be available before any child embed loads.
getNativeEmbedFacade().setup({
	experiences: gatedExperiences,
});

const buildNativeEmbedParameters = (
	url: string,
	parameterDefaults?: Partial<NativeEmbedParameterValues>,
) =>
	setParameters(
		{},
		{
			url,
			alwaysShowTitle: false,
			alignment: DEFAULT_ALIGNMENT,
			height: 600,
			width: undefined,
			...parameterDefaults,
		},
	);

const createRenderFn =
	(
		isPublishedPage: boolean,
		api?: NativeEmbedsEditorAPI,
		renderOverlay?: NativeEmbedOverlayRenderer,
	) =>
	async () => {
		const { default: Ui } = await import(
			/* webpackChunkName: "@atlassian/native-embeds-editor-extension" */ './ui'
		);
		return (props: React.ComponentProps<typeof Ui>) => (
			<Ui {...props} api={api} isPublishedPage={isPublishedPage} renderOverlay={renderOverlay} />
		);
	};

const createManifest = (
	isPublishedPage: boolean,
	api?: NativeEmbedsEditorAPI,
	cardClient?: CardClient,
	renderOverlay?: NativeEmbedOverlayRenderer,
): ExtensionManifest => {
	// Wire up the CardClient for short-link URL resolution when provided.
	// This is a separate call from setup() because the CardClient may not be
	// available at module-load time when setup() is called.
	if (cardClient) {
		getNativeEmbedFacade().setCardClient(cardClient);
	}

	return {
		title: 'Native Embed',
		type: NATIVE_EMBED_EXTENSION_TYPE,
		key: NATIVE_EMBED_EXTENSION_KEY,
		description: 'Embed native Atlassian content',
		modules: {
			nodes: {
				default: {
					type: 'extension',
					render: createRenderFn(isPublishedPage, api, renderOverlay),
					// @ts-expect-error This option is experimental and not reflected in the manifest type definition
					__hideFrame: true,
				},
				...Object.fromEntries(
					getNativeEmbedFacade()
						.getAllExperienceIds()
						.map((experienceId) => [
							experienceId,
							{
								type: 'extension',
								render: createRenderFn(isPublishedPage, api, renderOverlay),
								__hideFrame: true,
							},
						]),
				),
			},
			autoConvert: {
				url: [
					(url: string): ADFEntity | undefined => {
						// Check if URL matches any registered experience patterns
						const experienceId = getNativeEmbedFacade().getExperienceIdFromUrl(url);
						if (experienceId) {
							// Check if this experience should auto-convert to native embed
							const manifest = getNativeEmbedFacade().getManifestByUrl(url);
							if (!manifest || manifest.shouldAutoConvertToNativeEmbed !== true) {
								// Return undefined to allow smart link handling instead
								return undefined;
							}

							return {
								type: 'extension',
								attrs: {
									extensionKey: `${NATIVE_EMBED_EXTENSION_KEY}:${experienceId}`,
									extensionType: NATIVE_EMBED_EXTENSION_TYPE,
									parameters: buildNativeEmbedParameters(url, manifest?.parameterDefaults),
								},
							};
						}

						// Return undefined if URL doesn't match - handled by smart links instead.
						return undefined;
					},
				],
			},
		},
	};
};

export { createManifest };
